import React, { useCallback, useEffect, useState } from "react";
import * as z from "zod";
import { makeDashboardApiRequest } from "~/api_utilities";
import { Loader2 } from "~/embeds/components/Icons";

declare global {
  interface Window {
    XplGrowthHub: any;
  }
}

const XplorGrowthHubSchema = z.object({
  currentUser: z.object({
    currentAccount: z.object({
      metadata: z.object({
        marianatek: z.object({
          access_token: z.string(),
          refresh_token: z.string(),
          site: z.string(),
        }),
      }),
    }),
  }),
});

const LoadingScreen = () => {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="p-8 text-center">
        <h2 className="mb-4 text-2xl font-semibold text-gray-700">
          Welcome to Xplor Growth Loyalty
        </h2>
        <p className="mb-6 text-gray-500">Authenticating and loading your dashboard...</p>

        <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-500" />
      </div>
    </div>
  );
};

const AuthenticateResponseSchema = z.object({
  iframe_src: z.string(),
});

const authenticateWithXplor = async (
  businessId: string,
  accessToken: string,
  refreshToken: string,
) => {
  const result = await makeDashboardApiRequest(
    "/authenticate",
    "POST",
    {
      business_identifier: businessId,
      access_token: accessToken,
      refresh_token: refreshToken,
    },
    AuthenticateResponseSchema,
  );

  if (result.ok) {
    return result.value;
  }
};

export const XplorApp = () => {
  const [iframeSrc, setIframeSrc] = useState<string | null>();

  const [previousSite, setPreviousSite] = useState<string | null>(null);

  const checkForXplorBusiness = useCallback(async (): Promise<void> => {
    const parsed = XplorGrowthHubSchema.safeParse(window.XplGrowthHub);

    if (!parsed.success) {
      setIframeSrc(undefined);
      return;
    }

    const currentXplorSite = parsed.data.currentUser.currentAccount.metadata.marianatek.site;

    if (currentXplorSite !== previousSite) {
      setIframeSrc(null);
      const response = await authenticateWithXplor(
        parsed.data.currentUser.currentAccount.metadata.marianatek.site,
        parsed.data.currentUser.currentAccount.metadata.marianatek.access_token,
        parsed.data.currentUser.currentAccount.metadata.marianatek.refresh_token,
      );

      if (response) {
        setIframeSrc(response.iframe_src);
        setPreviousSite(currentXplorSite);
      }
    }
  }, [previousSite]);

  useEffect(() => {
    // sets up a method to continuously check whether we need to reload the iframe
    const checkInterval = setInterval(() => {
      checkForXplorBusiness();
    }, 1000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [checkForXplorBusiness]);

  if (iframeSrc === undefined) {
    return <LoadingScreen />;
  }

  return (
    <div className="size-full">
      {iframeSrc !== null && (
        <iframe
          src={iframeSrc}
          className="size-full border-none"
          title="Xplor Business Dashboard"
        />
      )}
    </div>
  );
};
